import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  AutoProcessor,
  CLIPVisionModelWithProjection,
  RawImage,
  env
} from '@xenova/transformers';
import pako from 'pako';
import './App.css';
import {
  getVideoUrl, getVideoPlatform, joinSnippets, cleanText,
  searchVideos, searchDms, sortResults, rerankImageMatches, MATCH,
  buildHighlighter, highlightSegments,
} from './searchUtils';
import './lucian/lucian-base.css';
import LucianToasts from './lucian/LucianToasts';
import LucianCopyFormat from './lucian/LucianCopyFormat';
import LucianImageDrop from './lucian/LucianImageDrop';
import LucianNoResults from './lucian/LucianNoResults';

// 로컬 모델 허용 여부 및 캐시 설정
env.allowLocalModels = true;
env.localModelPath = '/models/';
env.useBrowserCache = true;
// ONNX 런타임(wasm)도 셀프호스팅 — 기본값은 cdn.jsdelivr.net이라 CDN이 느리거나 차단되면 모델을 다 받고도 초기화가 멈춘다
env.backends.onnx.wasm.wasmPaths = '/wasm/';

// 물리적 화면 너비로 모바일 감지 (뷰포트 변경에 영향받지 않음)
if (window.screen.width < 768) {
  document.documentElement.setAttribute('data-mobile', 'true');
  document.documentElement.style.setProperty('--screen-w', `${window.screen.width}px`);
}

const SYNONYMS = {
  "새롬": ["롬새", "이새롬", "롬쌔", "캡틴", "세럼", "롬"],
  "하영": ["송하영", "하빵", "하냥", "송하빵"],
  "하빵": ["하영", "송하영"],
  "지원": ["박지원", "메건", "박메건"],
  "메건": ["지원", "박지원"],
  "지선": ["노지선", "노장금", "젠젠", "노지센"],
  "서연": ["이서연", "더연", "션", "이션"],
  "션": ["서연", "이서연"],
  "채영": ["이채영", "챙이", "모찌", "이챙"],
  "챙이": ["채영", "이채영"],
  "나경": ["이나경", "나꼬", "이나꼬"],
  "나꼬": ["나경", "이나경"],
  "지헌": ["백지헌", "꿀깅", "헌이", "백바둑"],
  "꿀깅": ["지헌", "백지헌"],
  "픎": ["프롬", "프로미스", "fromis", "프러미스", "나잇"],
  "플로버": ["플로바", "flover", "플로브", "플러버"],
  "노랑즈": ["노랑주", "노랑쥐",],
};

const FILE_COUNT = 3;
const BASE_URL = "https://raw.githubusercontent.com/iamgle001-lang/subtitle-data/refs/heads/main/posts_part_";
const NOTICE_URL = "https://raw.githubusercontent.com/iamgle001-lang/subtitle-data/main/notice.json";
const DM_DATA_URL = "https://raw.githubusercontent.com/iamgle001-lang/subtitle-data/refs/heads/main/dm_data.gz";
const MODEL_NAME = 'Xenova/clip-vit-base-patch32';
const COMPRESSED_DATA_URLS = Array.from({ length: FILE_COUNT }, (_, i) => `${BASE_URL}${i + 1}.json.gz`);
const ITEMS_PER_PAGE = 30;
const FUZZY_TRIGGER = 10; // 정확·동의어 일치 결과가 이 수 미만이면 받침 변형(퍼지) 검색으로 확장
const RECENT_KEY = 'f9_recent_searches';
const RECENT_MAX = 8;
const SUGGESTED_KEYWORDS = ['모찌', '꿀깅', '하빵', '메건', '롬새', '데이트', '배고파'];
const DM_ITEMS_PER_PAGE = 10;

// ── 모듈 레벨 순수 함수 ──────────────────────────────────────────────

// base64 시그니처로 이미지 MIME 타입 자동 감지
const getImageSrc = (b64) => {
  const mime = b64.startsWith('UklGR') ? 'image/webp' : 'image/jpeg';
  return `data:${mime};base64,${b64}`;
};

// 파일명을 사람이 읽기 좋은 제목으로 변환
const formatTitle = (filename) => {
  if (!filename) return '';
  return filename
    .replace(/\.(json|mp4|mov|ts|mkv|flv|wmv|webm|m4v|avi|m4a|jpg|jpeg|png|webp|bmp)$/i, '')
    .replace(/[_]/g, ' ')
    .trim();
};

// getVideoUrl, joinSnippets → searchUtils.js

// 영상 제목에서 연도 추출 (YYMMDD → 4자리 연도)
const getYearFromTitle = (title) => {
  if (!title) return null;
  const match = title.match(/(\d{6})/);
  if (!match) return null;
  const yy = parseInt(match[1].substring(0, 2));
  return yy >= 17 ? 2000 + yy : 1900 + yy;
};

// 영상 제목 정규화
const normalize = (name) =>
  name.replace(/\(vod\)|\[vod\]|\.(json|mp4|mov|ts|mkv|flv|wmv|webm|m4v|avi|m4a|jpg|jpeg|png|webp|bmp)/gi, "")
      .replace(/[^a-zA-Z0-9ㄱ-ㅎ가-힣]/g, "")
      .toLowerCase();

// 시간 포맷 (초 → M:SS)
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

// 멤버 CSS 클래스
const MEMBER_CLASS_MAP = {
  '새롬': 'sae', '하영': 'ha', '지원': 'jiw', '지선': 'jis',
  '서연': 'seo', '채영': 'che', '나경': 'nag', '지헌': 'jih'
};
const getMemberClass = (name) => MEMBER_CLASS_MAP[name] || 'default';

const sortDmByDate = (arr, multiplier) =>
  [...arr].sort((a, b) => {
    const dateA = parseInt(a.date.replace(/\./g, ''));
    const dateB = parseInt(b.date.replace(/\./g, ''));
    if (dateA === dateB) {
      return (parseInt(a.time.replace(':', '')) - parseInt(b.time.replace(':', ''))) * multiplier;
    }
    return (dateA - dateB) * multiplier;
  });

// 자막 파싱 (하위 호환성용) — 컴포넌트 외부 순수 함수
const parseSubtitles = (rawContent) => {
  if (!Array.isArray(rawContent)) return [];
  if (rawContent.length > 0 && typeof rawContent[0] === 'object') return rawContent;

  const parsed = [];
  let currentTime = null;
  let currentText = [];

  rawContent.forEach(line => {
    if (typeof line !== 'string') return;
    const trimmed = line.replace(/\r/g, '').trim();
    if (!trimmed) return;

    const srtMatch = trimmed.match(/(\d{1,2}):(\d{2}):(\d{2})[,.](\d{3})\s?-->/);
    const simpleTimeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);

    if (srtMatch) {
      if (currentTime !== null && currentText.length > 0) {
        parsed.push({ time: currentTime, text: currentText.join(" ") });
      }
      const h = parseInt(srtMatch[1]);
      const m = parseInt(srtMatch[2]);
      const s = parseInt(srtMatch[3]);
      const ms = parseInt(srtMatch[4]);
      currentTime = h * 3600 + m * 60 + s + (ms / 1000);
      currentText = [];
    } else if (simpleTimeMatch) {
      if (currentTime !== null && currentText.length > 0) {
        parsed.push({ time: currentTime, text: currentText.join(" ") });
      }
      const parts = trimmed.split(':').map(Number);
      if (parts.length === 2) currentTime = parts[0] * 60 + parts[1];
      else if (parts.length === 3) currentTime = parts[0] * 3600 + parts[1] * 60 + parts[2];
      currentText = [];
    } else if (!/^\d+$/.test(trimmed)) {
      if (currentTime !== null) currentText.push(trimmed);
    }
  });

  if (currentTime !== null && currentText.length > 0) {
    parsed.push({ time: currentTime, text: currentText.join(" ") });
  }
  return parsed;
};

// 카드 링크 속성 계산 (JSX IIFE 대체)
const getCardLinkProps = (item) => {
  const url = item.sourceUrl || item.metadata?.sourceUrl;
  if (!url) return null;
  const platform = getVideoPlatform(url);
  if (!platform) return null;
  return {
    href: platform === 'YouTube' ? getVideoUrl(url, item.metadata?.time) : url,
    cls: { YouTube: 'yt', Weverse: 'wv', fromm: 'fm' }[platform] || 'wv',
    platform,
  };
};

/**
 * 이미지 파일을 5가지 정사각형 크롭 Blob 배열로 변환
 * (센터, 좌상, 우상, 좌하, 우하)
 */
const getMultiCropBlobs = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const size = Math.min(w, h);
      // [sx, sy] 좌표 정의 (센터, 좌상, 우상, 좌하, 우하)
      const offsets = [
        [(w - size) / 2, (h - size) / 2], // center
        [0,           0          ],        // top-left
        [w - size,    0          ],        // top-right
        [0,           h - size   ],        // bottom-left
        [w - size,    h - size   ],        // bottom-right
      ];
      URL.revokeObjectURL(url);
      Promise.all(
        offsets.map(([sx, sy]) => new Promise((res, rej) => {
          const canvas = document.createElement('canvas');
          canvas.width  = size;
          canvas.height = size;
          canvas.getContext('2d').drawImage(img, sx, sy, size, size, 0, 0, size, size);
          canvas.toBlob(
            blob => blob ? res(blob) : rej(new Error('Canvas toBlob 실패')),
            'image/jpeg', 0.95
          );
        }))
      ).then(resolve).catch(reject);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('이미지 로드 실패')); };
    img.src = url;
  });

// YouTube URL에서 썸네일 URL 추출
const getYoutubeThumbnail = (url) => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    let videoId = null;
    if (parsed.hostname.includes('youtu.be')) {
      videoId = parsed.pathname.slice(1).split('?')[0];
    } else if (parsed.hostname.includes('youtube.com')) {
      videoId = parsed.searchParams.get('v');
    }
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
  } catch { return null; }
};

// ── IndexedDB 캐시 헬퍼 ──────────────────────────────────────────────
const DB_NAME    = 'fromis9-subtitle-cache';
const DB_VERSION = 1;
const DB_STORE   = 'files';

const openSubtitleDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, DB_VERSION);
  req.onupgradeneeded = e => {
    const db = e.target.result;
    if (db.objectStoreNames.contains(DB_STORE)) db.deleteObjectStore(DB_STORE);
    db.createObjectStore(DB_STORE);
  };
  req.onsuccess = e => resolve(e.target.result);
  req.onerror   = () => reject(req.error);
});

const idbGet = (db, key) => new Promise((resolve, reject) => {
  const req = db.transaction(DB_STORE, 'readonly').objectStore(DB_STORE).get(key);
  req.onsuccess = () => resolve(req.result);
  req.onerror   = () => reject(req.error);
});

const idbSet = (db, key, value) => new Promise((resolve, reject) => {
  const req = db.transaction(DB_STORE, 'readwrite').objectStore(DB_STORE).put(value, key);
  req.onsuccess = () => resolve();
  req.onerror   = () => reject(req.error);
});

// ── 컴포넌트 ────────────────────────────────────────────────────────
const ICON_PATHS = {
  search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
  camera: '<path d="M4 8h3l2-3h6l2 3h3v11H4z"/><circle cx="12" cy="13" r="3.5"/>',
  link: '<path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/>',
  play: '<path d="M7 4.5v15l12-7.5z"/>',
  copy: '<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/>',
  share: '<path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7"/><path d="M16 6l-4-4-4 4"/><path d="M12 2v13"/>',
  chat: '<path d="M4 5h16v11H8l-4 4z"/>',
  sortDesc: '<path d="M6 4v16"/><path d="m3 17 3 3 3-3"/><path d="M12 6h9"/><path d="M12 12h7"/><path d="M12 18h5"/>',
  sortAsc: '<path d="M6 20V4"/><path d="m3 7 3-3 3 3"/><path d="M12 6h5"/><path d="M12 12h7"/><path d="M12 18h9"/>',
  close: '<path d="M6 6l12 12"/><path d="M18 6 6 18"/>',
  home: '<path d="M3 11 12 3l9 8"/><path d="M5 10v10h14V10"/>',
  photo: '<rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="10" r="2"/><path d="m21 16-5-5-8 8"/>',
  script: '<path d="M6 3h9l4 4v14H6z"/><path d="M9 12h6"/><path d="M9 16h6"/>',
  clock: '<circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/>',
};

const Icon = ({ name, className = '' }) => (
  <svg className={`ic ${className}`} viewBox="0 0 24 24" aria-hidden="true" focusable="false"
    dangerouslySetInnerHTML={{ __html: ICON_PATHS[name] || '' }} />
);

const Highlight = ({ text, matcher }) => {
  if (!text) return null;
  if (!matcher) return text;
  return highlightSegments(text, matcher).map((s, i) =>
    s.hit ? <mark key={i} className="kw-mark">{s.text}</mark> : s.text
  );
};

function App() {

  // --- 상태 관리 ---
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [allSearchResults, setAllSearchResults] = useState([]);
  const [dmResults, setDmResults] = useState([]);
  const [isDmExpanded, setIsDmExpanded] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [searchElapsedMs, setSearchElapsedMs] = useState(null);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isModelReady, setIsModelReady] = useState(false);
  const [modelProgress, setModelProgress] = useState(null); // { loaded, total } — onnx 파일 다운로드 진행
  const [modelError, setModelError] = useState(null);
  const [loadStatus, setLoadStatus] = useState({ percent: 0, message: '시스템 초기화 중...' });
  const [hasSearched, setHasSearched] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [notice, setNotice] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [dmModalData, setDmModalData] = useState(null);

  const [sortOrder, setSortOrder] = useState('desc');
  const [activeSearch, setActiveSearch] = useState(null); // { tokens, fuzzy } — 하이라이트 기준
  const [recentSearches, setRecentSearches] = useState(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
  });
  const [expandedCardId, setExpandedCardId] = useState(null);
  // key: `${cardId}-${idx}`, value: snippet text
  const [selectedSnippets, setSelectedSnippets] = useState(new Map());
  // 모달 대본 선택 복사: key = idx, value = { text, time }
  const [modalSelectedLines, setModalSelectedLines] = useState(new Map());
  // 복사 형식 선택 팝업
  const [copyFormatPicker, setCopyFormatPicker] = useState(false);

  const [dmPage, setDmPage] = useState(1);
  const [page, setPage] = useState(1);
  const [selectedYear, setSelectedYear] = useState(null);
  const toastRef = useRef(null);
  const [imageJobs, setImageJobs] = useState([]); // 사진 검색 진행 목록 (최근 3개)
  const [recommendedVideos, setRecommendedVideos] = useState([]);
  const [videoThumbMap, setVideoThumbMap] = useState({});

  // Refs
  const processorRef    = useRef(null);
  const visionModelRef  = useRef(null);
  const searchInputRef  = useRef(null);
  const scriptContainerRef = useRef(null);
  const dmContextRef    = useRef(null);
  const modalHistoryRef = useRef(false);
  const imageHistoryRef = useRef(false);
  // 데이터는 ref로 관리: 로딩 중 리렌더 방지
  const allVideosRef    = useRef([]);
  const allDmsRef       = useRef([]);

  // ── 메모이제이션 ──────────────────────────────────────────────────

  // 연도 필터 목록 (allSearchResults 변경 시만 재계산)
  const availableYears = useMemo(() =>
    [...new Set(
      allSearchResults.map(item => getYearFromTitle(item.metadata?.video)).filter(Boolean)
    )].sort(),
    [allSearchResults]
  );

  // DM 하이라이트 regex (다중 키워드 OR 매칭)
  // 결과 0건일 때 #092 제안: 다중 키워드는 "단어 하나 빼면 N건", 단일 키워드는 끝 글자를 줄인 검색어
  const noResultHints = useMemo(() => {
    const empty = { drops: [], shorter: null };
    if (!activeSearch || allSearchResults.length || dmResults.length) return empty;
    const countFor = toks => {
      const v = searchVideos(allVideosRef.current, toks, SYNONYMS);
      const vn = v.length >= FUZZY_TRIGGER ? v.length : searchVideos(allVideosRef.current, toks, SYNONYMS, { fuzzy: true }).length;
      const d = searchDms(allDmsRef.current, toks, SYNONYMS);
      const dn = d.length >= FUZZY_TRIGGER ? d.length : searchDms(allDmsRef.current, toks, SYNONYMS, { fuzzy: true }).length;
      return vn + dn;
    };
    const toks = activeSearch.tokens;
    if (toks.length > 1) {
      const drops = toks.map(w => {
        const rest = toks.filter(x => x !== w);
        return { word: w, rest: rest.join(' '), count: countFor(rest) };
      }).filter(d => d.count > 0).sort((a, b) => b.count - a.count);
      return { drops, shorter: null };
    }
    const w = toks[0] || '';
    if (w.length >= 3) {
      const term = w.slice(0, -1);
      const count = countFor([term]);
      if (count > 0) return { drops: [], shorter: { term, count } };
    }
    return empty;
  }, [activeSearch, allSearchResults.length, dmResults.length]);

  // 전체 대본 모달의 #008 복사 형식: 구간 링크 / 대사+링크 / 대사만 (가능한 것만)
  const copyFormats = useMemo(() => {
    if (!modalData) return [];
    const t = modalData.targetTime || 0;
    const title = modalData.metadata?.video || '';
    const script = Array.isArray(modalData.fullScript) ? modalData.fullScript : [];
    const near = script.reduce((best, l) => (!best || Math.abs(l.time - t) < Math.abs(best.time - t) ? l : best), null);
    const line = (modalData.snippet || near?.text || '').trim();
    const src = modalData.sourceUrl || '';
    const platform = getVideoPlatform(src);
    const tsUrl = src && platform !== 'fromm' ? getVideoUrl(src, t) : src;
    const out = [];
    if (tsUrl) {
      let host = tsUrl;
      try { const u = new URL(tsUrl); host = u.host.replace(/^www\./, '') + u.pathname + u.search; } catch {}
      out.push({ key: 'link', label: platform === 'fromm' ? '영상 링크' : '구간 링크', em: platform === 'fromm' ? '링크' : `▶ ${formatTime(t)}`, cmd: host, b: '', full: tsUrl });
    }
    if (line && tsUrl) out.push({ key: 'both', label: '대사+링크', em: `[${formatTime(t)}]`, cmd: line, b: '', full: `${title}\n[${formatTime(t)}] ${line}\n\n${tsUrl}` });
    if (line) out.push({ key: 'line', label: '대사만', em: '“', cmd: line, b: '”', full: line });
    return out;
  }, [modalData]);

  const highlighter = useMemo(
    () => (activeSearch ? buildHighlighter(activeSearch.tokens, SYNONYMS, activeSearch.fuzzy) : null),
    [activeSearch]
  );

  // ── 유틸 ──────────────────────────────────────────────────────────

  // 문자열이면 앞 이모지를 떼고 실패/오류 문구는 오류 알림으로. 객체면 #070 push 옵션 그대로
  const showToast = useCallback((msg, extra) => {
    const opts = typeof msg === 'string'
      ? { title: msg.replace(/^(?:[←-⯿️\s]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDC00-\uDFFF])+/, '').trim(), kind: /실패|오류/.test(msg) ? 'err' : 'ok', ...extra }
      : msg;
    return toastRef.current?.push(opts);
  }, []);

  const handleThumbLoad = useCallback((e) => {
    const img = e.target;
    if (img.naturalHeight > img.naturalWidth) {
      img.classList.add('portrait-thumb');
    }
  }, []);

  // ── AI 모델 로드 (이미지 검색) ──────────────────────────────────────
  // 실패 원인은 대개 네트워크 중단·느린 회선·손상된 Cache API 항목이라 재시도 + 캐시 비우기 + 진행률 표시로 대응
  const loadModel = useCallback(async () => {
    setModelError(null);
    setModelProgress(null);
    const MAX_TRY = 3;
    const STALL_MS = 90000;
    let stallTimer = null;
    const armStall = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => setModelError('다운로드가 90초 동안 진행되지 않았습니다.'), STALL_MS);
    };
    const progress_callback = (p) => {
      if (p.status === 'progress' && typeof p.file === 'string' && p.file.endsWith('.onnx')) {
        setModelProgress({ loaded: p.loaded, total: p.total });
      }
      armStall();
    };
    armStall();
    try {
      for (let attempt = 1; attempt <= MAX_TRY; attempt++) {
        try {
          processorRef.current = await AutoProcessor.from_pretrained(MODEL_NAME, { progress_callback });
          visionModelRef.current = await CLIPVisionModelWithProjection.from_pretrained(MODEL_NAME, { progress_callback });
          setModelError(null);
          setIsModelReady(true);
          return;
        } catch (e) {
          console.error(`AI 모델 로드 실패 (${attempt}/${MAX_TRY}):`, e);
          if (attempt === MAX_TRY) {
            setModelError(e?.message || String(e));
            return;
          }
          if (attempt === MAX_TRY - 1 && window.caches) {
            try { await caches.delete('transformers-cache'); } catch {}
          }
          await new Promise(r => setTimeout(r, 1500 * attempt));
        }
      }
    } finally {
      clearTimeout(stallTimer);
    }
  }, []);

  // ── 초기 데이터 로드 ───────────────────────────────────────────────
  useEffect(() => {
    // 데이터 로드 (텍스트 검색 준비)
    const loadData = async () => {
      let db = null;
      try { db = await openSubtitleDB(); } catch (_) {}

      try {
        fetch(NOTICE_URL + '?t=' + new Date().getTime())
          .then(res => res.json())
          .then(data => { if (data && data.show) setNotice(data); })
          .catch(() => {});

        setLoadStatus({ percent: 5, message: '데이터 로드 시작...' });

        // DM 데이터 gz 압축 해제 후 로드
        fetch(DM_DATA_URL)
          .then(res => res.arrayBuffer())
          .then(buffer => {
            const decompressed = pako.ungzip(buffer, { to: 'string' });
            const data = JSON.parse(decompressed);
            if (data && data.length > 0) {
              for (const msg of data) msg.clean = cleanText(msg.content);
              allDmsRef.current = data;
            }
          })
          .catch(() => {});

        let loadedCount = 0;
        // 로컬 누산 배열: 병렬 로드 시 race condition 없이 안전하게 수집
        const videosAccum = [];

        const loadPromises = COMPRESSED_DATA_URLS.map(async (url, fileIndex) => {
          const cacheKey = `file_${fileIndex}`;
          let jsonData = null;

          if (db) {
            try { jsonData = await idbGet(db, cacheKey); } catch (_) {}
          }

          if (!jsonData) {
            try {
              const res = await fetch(url);
              if (!res.ok) { console.warn(`File missing: ${url}`); return; }
              const buffer = await res.arrayBuffer();
              if (!buffer) return;
              const decompressed = pako.ungzip(buffer, { to: 'string' });
              jsonData = JSON.parse(decompressed);
              if (db) idbSet(db, cacheKey, jsonData).catch(() => {});
            } catch (err) {
              console.error(`Error loading ${url}`, err);
              return;
            }
          }

          const processedVideos = jsonData.map(video => {
            let rawContent = video.content;
            let sourceUrl = null;

            if (Array.isArray(rawContent) && rawContent.length > 0) {
              const last = rawContent[rawContent.length - 1];
              if (typeof last === 'string' && last.startsWith('http')) {
                sourceUrl = last;
                rawContent = rawContent.slice(0, -1);
              }
            }
            if (!sourceUrl && Array.isArray(rawContent)) {
              for (let i = rawContent.length - 1; i >= 0; i--) {
                const item = rawContent[i];
                if (item && typeof item.text === 'string') {
                  const m = item.text.match(/https?:\/\/\S+/);
                  if (m) {
                    sourceUrl = m[0];
                    const textWithoutUrl = item.text.replace(/https?:\/\/\S+/g, '').trim();
                    if (!textWithoutUrl) {
                      rawContent = rawContent.filter((_, idx) => idx !== i);
                    } else {
                      rawContent = rawContent.map((c, idx) =>
                        idx === i ? { ...c, text: textWithoutUrl } : c
                      );
                    }
                    break;
                  }
                }
              }
            }

            const content = (typeof rawContent === 'string' || (Array.isArray(rawContent) && typeof rawContent[0] === 'string'))
              ? parseSubtitles(rawContent)
              : rawContent;
            if (Array.isArray(content)) {
              // 검색 때마다 정규화하지 않도록 1회 전처리. IDB put(구조적 복제)은 이미 끝난 뒤라 캐시에는 들어가지 않음
              for (const line of content) if (line && typeof line === 'object') line.clean = cleanText(line.text);
            }
            return { ...video, sourceUrl, content };
          });

          videosAccum.push(...processedVideos);
          loadedCount++;
          setLoadStatus({
            percent: 5 + Math.round((95 / FILE_COUNT) * loadedCount),
            message: loadedCount < FILE_COUNT
              ? `데이터 로드 중... (${loadedCount}/${FILE_COUNT})`
              : '텍스트 검색 준비 완료!'
          });
        });

        await Promise.all(loadPromises);
        allVideosRef.current = videosAccum;
        setIsDataReady(true);

      } catch (e) { console.error(e); setLoadStatus({ percent: 0, message: '초기화 오류' }); }
    };

    loadData();
    loadModel(); // 데이터 로드와 병렬
  }, [loadModel]);

  // 추천 영상 + 썸네일 맵: 데이터 로드 완료 후 1회 구성
  useEffect(() => {
    if (!isDataReady) return;

    const withUrl = allVideosRef.current.filter(v => v.sourceUrl || v.thumb);
    const shuffled = [...withUrl].sort(() => Math.random() - 0.5);
    setRecommendedVideos(shuffled.slice(0, 12));

    // title → thumb URL 맵 (make_thumbs.py 실행 후 posts.json에 추가된 필드)
    const map = {};
    allVideosRef.current.forEach(v => {
      if (v.thumb) map[v.title] = v.thumb;
    });
    setVideoThumbMap(map);
  }, [isDataReady]);

  // 딥링크 처리 (isDataReady 후 1회 실행)
  useEffect(() => {
    if (!isDataReady) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = parseInt(params.get('id'));
    const targetVideoParam = params.get('video');
    const targetTime = parseFloat(params.get('time'));

    if (!isNaN(targetTime)) {
      let matchedVideo = null;

      if (!isNaN(targetId)) {
        matchedVideo = allVideosRef.current.find(v => v.id === targetId);
      }
      if (!matchedVideo && targetVideoParam) {
        const targetClean = normalize(targetVideoParam);
        matchedVideo = allVideosRef.current.find(v => {
          const vClean = normalize(v.title);
          return vClean === targetClean || vClean.includes(targetClean) || targetClean.includes(vClean);
        });
      }

      if (matchedVideo) {
        let fullScript = matchedVideo.content;
        if (typeof fullScript === 'string' || (Array.isArray(fullScript) && typeof fullScript[0] === 'string')) {
          fullScript = parseSubtitles(fullScript);
        }
        setModalData({
          metadata: { video: matchedVideo.title, time: targetTime, preview: null },
          fullScript,
          targetTime
        });
        window.history.replaceState({}, document.title, "/");
      }
    }
  }, [isDataReady]);

  useEffect(() => {
    if (isModelReady && searchInputRef.current) searchInputRef.current.focus();
  }, [isModelReady]);


  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  useEffect(() => {
    if (modalData && scriptContainerRef.current) {
      const highlightedElement = scriptContainerRef.current.querySelector('.highlight-main');
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (modalData.targetTime != null && modalData.fullScript?.length > 0) {
        const closest = scriptContainerRef.current.querySelector(
          `[data-time="${modalData.fullScript.reduce((prev, cur) =>
            Math.abs(cur.time - modalData.targetTime) < Math.abs(prev.time - modalData.targetTime) ? cur : prev
          ).time}"]`
        );
        if (closest) closest.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [modalData]);

  useEffect(() => {
    if (dmModalData && dmContextRef.current) {
      const targetElement = dmContextRef.current.querySelector('.dm-highlight');
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [dmModalData]);

  // 이미지 검색 시 history pushState
  useEffect(() => {
    if (selectedImage && !imageHistoryRef.current) {
      window.history.pushState({ imageSearch: true }, '');
      imageHistoryRef.current = true;
    }
  }, [selectedImage]);

  // 모달 열릴 때 history pushState
  useEffect(() => {
    if ((modalData || dmModalData) && !modalHistoryRef.current) {
      window.history.pushState({ modal: true }, '');
      modalHistoryRef.current = true;
    }
  }, [modalData, dmModalData]);

  // 뷰포트 강제 리셋
  const resetMobileViewport = useCallback(() => {
    const vm = document.querySelector('meta[name=viewport]');
    if (!vm) return;
    vm.content = 'width=device-width, initial-scale=1, maximum-scale=1';
    setTimeout(() => { vm.content = 'width=device-width, initial-scale=1'; }, 200);
    setTimeout(() => { vm.content = 'width=device-width, initial-scale=1'; }, 600);
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') resetMobileViewport();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [resetMobileViewport]);

  useEffect(() => {
    const handlePopState = () => {
      if (modalHistoryRef.current) {
        setModalData(null);
        setDmModalData(null);
        modalHistoryRef.current = false;
        resetMobileViewport();
      } else if (imageHistoryRef.current) {
        setSelectedImage(null);
        setPreviewUrl(null);
        setResults([]);
        setAllSearchResults([]);
        setDmResults([]);
        setHasSearched(false);
        imageHistoryRef.current = false;
        resetMobileViewport();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [resetMobileViewport]);

  const closeModal = useCallback(() => {
    setModalSelectedLines(new Map());
    setCopyFormatPicker(false);
    if (modalHistoryRef.current) {
      window.history.back();
    } else {
      setModalData(null);
    }
  }, []);

  const closeDmModal = useCallback(() => {
    if (modalHistoryRef.current) {
      window.history.back();
    } else {
      setDmModalData(null);
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (copyFormatPicker) setCopyFormatPicker(false);
      else if (modalData) closeModal();
      else if (dmModalData) closeDmModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [copyFormatPicker, modalData, dmModalData, closeModal, closeDmModal]);

  // 링크 복사
  const handleCopyLink = useCallback((e, item) => {
    e.stopPropagation();
    const videoId = item.id;
    const time = item.metadata.time;
    const link = videoId
      ? `${window.location.origin}?id=${videoId}&time=${time}`
      : `${window.location.origin}?video=${encodeURIComponent(item.metadata.video)}&time=${time}`;
    navigator.clipboard.writeText(link)
      .then(() => showToast("🔗 링크가 복사되었습니다."))
      .catch(() => showToast("클립보드 복사에 실패했습니다."));
  }, [showToast]);

  // 대사 복사
  const handleCopyScript = useCallback((text) => {
    navigator.clipboard.writeText(text)
      .then(() => showToast("📋 대사가 복사되었습니다!"))
      .catch(() => showToast("클립보드 복사에 실패했습니다."));
  }, [showToast]);

  // 선택적 대사 복사
  const handleSelectiveCopy = useCallback(() => {
    const sorted = [...selectedSnippets.entries()]
      .sort((a, b) => {
        const ai = parseInt(a[0].split('-').pop(), 10);
        const bi = parseInt(b[0].split('-').pop(), 10);
        return ai - bi;
      });

    let cardTitle = '';
    let cardSourceUrl = '';
    let firstTime = 0;

    if (expandedCardId) {
      const expandedCard = results.find(item => (item.id ?? item.metadata?.video) === expandedCardId);
      if (expandedCard) {
        cardTitle = expandedCard.metadata?.video || '';
        cardSourceUrl = expandedCard.sourceUrl || '';

        // 첫 선택된 항목의 시간 추출
        if (sorted.length > 0) {
          const firstKey = sorted[0][0];
          const firstIdx = parseInt(firstKey.split('-').pop(), 10);
          if (expandedCard.subMatches && expandedCard.subMatches[firstIdx]) {
            firstTime = expandedCard.subMatches[firstIdx].metadata?.time || 0;
          }
        }
      }
    }

    const snippets = sorted.map(([, text]) => text);
    let textToCopy = joinSnippets(snippets);

    // 제목 추가
    if (cardTitle) {
      textToCopy = `${cardTitle}\n${textToCopy}`;
    }

    // 타임스탬프 링크 추가 (YouTube, Weverse 등)
    if (cardSourceUrl && (cardSourceUrl.includes('youtube.com') || cardSourceUrl.includes('youtu.be') || cardSourceUrl.includes('weverse.io'))) {
      const tsUrl = getVideoUrl(cardSourceUrl, firstTime);
      if (tsUrl && tsUrl !== cardSourceUrl) {
        textToCopy = `${textToCopy}\n\n${tsUrl}`;
      }
    }

    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        showToast(`📋 ${sorted.length}개 대사가 복사되었습니다!`);
        setSelectedSnippets(new Map());
      })
      .catch(() => showToast('클립보드 복사에 실패했습니다.'));
  }, [selectedSnippets, showToast, expandedCardId, results]);

  // 모달 선택 복사 실행 (포맷 선택 후 호출)
  const performModalCopy = useCallback((withTimestamp) => {
    const sorted = [...modalSelectedLines.entries()].sort((a, b) => a[0] - b[0]);
    const lines = sorted.map(([, v]) =>
      withTimestamp ? `[${formatTime(v.time)}] ${v.text}` : v.text
    );

    const videoTitle = modalData?.metadata?.video || '';
    const sourceUrl = modalData?.sourceUrl || '';
    const firstTime = sorted.length > 0 ? sorted[0][1].time : 0;

    let textToCopy = lines.join('\n');

    // 제목 추가
    if (videoTitle) {
      textToCopy = `${videoTitle}\n${textToCopy}`;
    }

    // 타임스탬프 링크 추가 (YouTube, Weverse 등)
    if (sourceUrl && (sourceUrl.includes('youtube.com') || sourceUrl.includes('youtu.be') || sourceUrl.includes('weverse.io'))) {
      const tsUrl = getVideoUrl(sourceUrl, firstTime);
      if (tsUrl && tsUrl !== sourceUrl) {
        textToCopy = `${textToCopy}\n\n${tsUrl}`;
      }
    }

    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        showToast(`📋 ${sorted.length}줄이 복사되었습니다!`);
        setModalSelectedLines(new Map());
        setCopyFormatPicker(false);
      })
      .catch(() => showToast('클립보드 복사에 실패했습니다.'));
  }, [modalSelectedLines, showToast, modalData]);

  // 모달 버튼 클릭 시: 선택 항목 있으면 포맷 팝업, 없으면 단일 복사
  const handleModalLineCopy = useCallback((e, singleText) => {
    e.stopPropagation();
    if (modalSelectedLines.size > 0) {
      setCopyFormatPicker(true);
    } else {
      handleCopyScript(singleText);
    }
  }, [modalSelectedLines, handleCopyScript]);

  // 타임스탬프 링크 공유 (Web Share API → 클립보드 fallback)
  const handleShare = useCallback((e, url, title) => {
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({ title: title || '대사 링크', url })
        .catch(() => {});
    } else {
      navigator.clipboard.writeText(url)
        .then(() => showToast('🔗 링크가 복사되었습니다.'))
        .catch(() => showToast('클립보드 복사에 실패했습니다.'));
    }
  }, [showToast]);

  // 이미지 검색
  const executeIntegratedSearch = useCallback(async (file) => {
    const searchStart = performance.now();
    const jobId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const setJob = patch => setImageJobs(prev => prev.map(j => (j.id === jobId ? { ...j, ...patch } : j)));
    setImageJobs(prev => [{ id: jobId, name: file.name || '붙여넣은 이미지', file, p: 0.04, state: 'run', note: '장면 특징 읽는 중' }, ...prev.filter(j => j.file !== file)].slice(0, 3));
    setIsLoading(true);
    setHasSearched(true);
    setSearchElapsedMs(null);
    setActiveSearch(null);
    setResults([]);
    setAllSearchResults([]);
    setDmResults([]);
    setExpandedCardId(null);
    setDmPage(1);
    setIsDmExpanded(false);

    try {
      const getEmbedding = async (blob) => {
        const raw = await RawImage.fromBlob(blob);
        const inputs = await processorRef.current(raw);
        const { image_embeds } = await visionModelRef.current(inputs);
        return Array.from(image_embeds.data);
      };

      // 5크롭(센터·좌상·우상·좌하·우하) + 전체 이미지 앙상블
      const cropBlobs = await getMultiCropBlobs(file);
      setJob({ p: 0.15 });
      let doneVecs = 0;
      const total = cropBlobs.length + 1;
      const track = pr => pr.then(v => { doneVecs++; setJob({ p: 0.15 + 0.55 * (doneVecs / total), note: `장면 특징 읽는 중 · ${doneVecs}/${total}` }); return v; });
      const allVecs = await Promise.all([
        ...cropBlobs.map(blob => track(getEmbedding(blob))),
        track(getEmbedding(file)),
      ]);
      setJob({ p: 0.78, note: '비슷한 장면 찾는 중' });

      const dim = allVecs[0].length;
      const avg = new Array(dim).fill(0);
      for (const vec of allVecs) {
        for (let i = 0; i < dim; i++) avg[i] += vec[i];
      }
      for (let i = 0; i < dim; i++) avg[i] /= allVecs.length;

      const norm = Math.sqrt(avg.reduce((s, v) => s + v * v, 0));
      const queryVector = norm > 0 ? avg.map(v => v / norm) : avg;

      const response = await fetch('/.netlify/functions/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vector: queryVector }),
      });

      if (!response.ok) throw new Error('서버 응답 오류');
      const data = await response.json();
      const finalMatches = rerankImageMatches(data.matches || []);
      setAllSearchResults(finalMatches);
      setResults(finalMatches);
      const secs = ((performance.now() - searchStart) / 1000).toFixed(1);
      setJob({ p: 1, state: 'done', note: finalMatches.length ? `비슷한 장면 ${finalMatches.length}개 · ${secs}초` : `비슷한 장면 없음 · ${secs}초` });

    } catch (err) {
      console.error(err);
      setJob({ state: 'fail', note: err?.message === '서버 응답 오류' ? '서버 응답이 없어요 · 다시 시도해 주세요' : '이미지를 읽지 못했어요' });
      showToast({ kind: 'err', title: '사진 검색에 실패했어요', sub: '잠시 뒤 다시 시도해 주세요', action: '다시 시도', onAction: () => executeIntegratedSearchRef.current?.(file) });
    } finally {
      setSearchElapsedMs(Math.round(performance.now() - searchStart));
      setIsLoading(false);
    }
  }, [showToast]);

  const executeIntegratedSearchRef = useRef(null);
  executeIntegratedSearchRef.current = executeIntegratedSearch;

  const handleImageFile = useCallback(async (file) => {
    if (file && isModelReady) {
      const vm = document.querySelector('meta[name=viewport]');
      if (vm) {
        vm.content = 'width=device-width, initial-scale=1, maximum-scale=1';
        setTimeout(() => { vm.content = 'width=device-width, initial-scale=1'; }, 300);
        setTimeout(() => { vm.content = 'width=device-width, initial-scale=1'; }, 800);
      }
      setPreviewUrl(URL.createObjectURL(file));
      setSelectedImage(file);
      setQuery('');
      await executeIntegratedSearch(file);
    }
  }, [isModelReady, executeIntegratedSearch]);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    e.target.value = '';
    handleImageFile(file);
  }, [handleImageFile]);

  // 텍스트 검색 (allVideos/allDms는 ref → deps 단순화)
  const handleTextSearch = useCallback((term) => {
    const q = (typeof term === 'string' ? term : query).trim();
    if (!q) return;
    if (q !== query) setQuery(q);
    const searchStart = performance.now();
    setIsLoading(true);
    setHasSearched(true);
    setSearchElapsedMs(null);
    setSelectedYear(null);
    setDmResults([]);
    setExpandedCardId(null);
    setDmPage(1);
    setIsDmExpanded(false);

    const tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
    const videos = allVideosRef.current;
    const dms = allDmsRef.current;

    // 영상 검색: 정확·동의어 일치가 적을 때만 퍼지 검색으로 확장
    let matched = searchVideos(videos, tokens, SYNONYMS);
    const fuzzyUsed = matched.length < FUZZY_TRIGGER;
    if (fuzzyUsed) matched = searchVideos(videos, tokens, SYNONYMS, { fuzzy: true });

    const groupedResults = {};
    matched.forEach(({ video, hits, tier }) => {
      const key = video.id ?? video.title;
      if (!groupedResults[key]) groupedResults[key] = [];
      hits.forEach(({ line }) => {
        groupedResults[key].push({
          metadata: { video: video.title, time: line.time, preview: "" },
          id: video.id,
          score: tier,
          snippet: line.text,
          sourceUrl: video.sourceUrl || null
        });
      });
    });

    const sortMultiplier = sortOrder === 'desc' ? -1 : 1;
    const finalGroupedResults = sortResults(
      Object.values(groupedResults).map(group => {
        group.sort((a, b) => a.metadata.time - b.metadata.time);
        return { ...group[0], subMatches: group.slice(1), totalCount: group.length };
      }),
      sortMultiplier
    );

    // DM 검색: 같은 기준으로 퍼지 확장, 정확 일치를 앞에 배치
    let dmMatched = searchDms(dms, tokens, SYNONYMS);
    if (dmMatched.length < FUZZY_TRIGGER) dmMatched = searchDms(dms, tokens, SYNONYMS, { fuzzy: true });
    const dmMatches = dmMatched.sort((a, b) => b.tier - a.tier).map(({ msg }) => msg);

    setAllSearchResults(finalGroupedResults);
    setResults(finalGroupedResults.slice(0, ITEMS_PER_PAGE));
    setPage(1);
    setDmResults(dmMatches);
    setActiveSearch({ tokens, fuzzy: fuzzyUsed });
    setRecentSearches(prev => {
      const next = [q, ...prev.filter(s => s !== q)].slice(0, RECENT_MAX);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    setSearchElapsedMs(Math.round(performance.now() - searchStart));
    setIsLoading(false);
  }, [query, sortOrder]);

  const getYearFiltered = useCallback((year) => {
    if (!year) return allSearchResults;
    return allSearchResults.filter(item => getYearFromTitle(item.metadata?.video) === year);
  }, [allSearchResults]);

  const handleYearFilter = useCallback((year) => {
    setSelectedYear(year);
    setPage(1);
    setResults(getYearFiltered(year).slice(0, ITEMS_PER_PAGE));
  }, [getYearFiltered]);

  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setResults(getYearFiltered(selectedYear).slice(0, nextPage * ITEMS_PER_PAGE));
    setPage(nextPage);
  }, [page, selectedYear, getYearFiltered]);

  const toggleSort = useCallback(() => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    const multiplier = newOrder === 'desc' ? -1 : 1;
    setSortOrder(newOrder);

    if (allSearchResults.length > 0) {
      const sorted = sortResults(allSearchResults, multiplier);
      setAllSearchResults(sorted);
      setResults(sorted.slice(0, page * ITEMS_PER_PAGE));
    }
    if (dmResults.length > 0) {
      setDmResults(sortDmByDate(dmResults, multiplier));
    }
  }, [sortOrder, allSearchResults, dmResults, page]);

  // 카드 클릭 (allVideos ref 사용 → deps 불필요)
  const handleCardClick = useCallback((item) => {
    const targetTime  = item.metadata.time;
    const targetId    = item.id;
    const targetVideo = item.metadata.video || "";
    let matchedVideo  = null;

    if (typeof targetId === 'number') {
      matchedVideo = allVideosRef.current.find(v => v.id === targetId);
    }
    if (!matchedVideo && targetVideo) {
      const targetClean = normalize(targetVideo);
      matchedVideo = allVideosRef.current.find(v => {
        const vClean = normalize(v.title);
        return vClean === targetClean || vClean.includes(targetClean) || targetClean.includes(vClean);
      });
    }
    if (!matchedVideo && targetVideo) {
      const stripCentury = (s) => s.replace(/20(\d{6})/g, '$1');
      const targetNorm   = stripCentury(normalize(targetVideo));
      const tDate        = targetNorm.match(/\d{6}/)?.[0];
      const tTokens      = (targetNorm.replace(/\d+/g, '').match(/[a-zA-Z가-힣]+/g) || []);

      let bestScore = 0;
      let bestVideo = null;

      allVideosRef.current.forEach(v => {
        const vNorm   = stripCentury(normalize(v.title));
        const vDate   = vNorm.match(/\d{6}/)?.[0];
        if (tDate && vDate && tDate !== vDate) return;

        const vTokens = (vNorm.replace(/\d+/g, '').match(/[a-zA-Z가-힣]+/g) || []);
        if (tTokens.length === 0 || vTokens.length === 0) return;

        const overlap = tTokens.filter(t => vTokens.includes(t)).length;
        const score   = overlap / Math.max(tTokens.length, vTokens.length);
        if (score > bestScore) { bestScore = score; bestVideo = v; }
      });

      if (bestScore >= 0.35) matchedVideo = bestVideo;
    }

    let fullScript    = [];
    let noScriptReason = null;

    if (!matchedVideo) {
      noScriptReason = 'not_found';
    } else if (!matchedVideo.content || matchedVideo.content.length === 0) {
      noScriptReason = 'no_content';
    } else {
      fullScript = matchedVideo.content;
      if (typeof fullScript === 'string' || (Array.isArray(fullScript) && typeof fullScript[0] === 'string')) {
        fullScript = parseSubtitles(fullScript);
      }
      fullScript = fullScript.sort((a, b) => a.time - b.time);
    }

    setModalData({ ...item, fullScript, targetTime, noScriptReason, sourceUrl: matchedVideo?.sourceUrl || item.metadata?.sourceUrl || null });
  }, []);

  // DM 클릭 (allDms ref 사용)
  const handleDmClick = useCallback((selectedMsg) => {
    const memberDms = allDmsRef.current.filter(msg => msg.member === selectedMsg.member);
    const index = memberDms.indexOf(selectedMsg);
    if (index === -1) return;

    const start = Math.max(0, index - 50);
    const end   = Math.min(memberDms.length, index + 51);
    setDmModalData({
      target: selectedMsg,
      context: memberDms.slice(start, end)
    });
  }, []);

  // filteredTotal (getYearFiltered는 useCallback이므로 안전하게 호출)
  const filteredTotal = useMemo(
    () => getYearFiltered(selectedYear).length,
    [getYearFiltered, selectedYear]
  );

  // ── 렌더 ─────────────────────────────────────────────────────────

  return (
    <div className="App">
      <LucianToasts ref={toastRef} />

      {!isDataReady && (
        <div className="loading-overlay">
          <div className="loading-box">
            <div className="spinner"></div>
            <h3>{loadStatus.message}</h3>
            <div className="progress-bar-container">
              <div className="progress-bar-fill" style={{ width: `${loadStatus.percent}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* 영상 모달 */}
      {modalData && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={closeModal} aria-label="닫기"><Icon name="close" /></button>
            {modalData.metadata?.preview && (
              <img src={getImageSrc(modalData.metadata.preview)} alt="Detail" className="modal-img result-thumb" />
            )}
            <div className="modal-header">
              <h3 title={modalData.metadata?.video}>{formatTitle(modalData.metadata?.video)}</h3>
              <div className="modal-meta-row">
                <span className="modal-time"><Icon name="clock" /> {formatTime(modalData.targetTime)} 구간</span>
                {modalData.sourceUrl && (() => {
                  const platform = getVideoPlatform(modalData.sourceUrl);
                  const cls = { YouTube: 'yt', Weverse: 'wv', fromm: 'fm' }[platform] || 'wv';
                  const href = platform === 'YouTube'
                    ? getVideoUrl(modalData.sourceUrl, modalData.targetTime)
                    : modalData.sourceUrl;
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className={`video-link-btn ${cls}`}>
                      <Icon name="play" /> {platform}에서 보기
                    </a>
                  );
                })()}
              </div>
              {copyFormats.length > 0 && (
                <LucianCopyFormat
                  formats={copyFormats}
                  onCopied={(ok, f) => showToast(ok
                    ? { kind: 'ok', title: `${f.label} 복사했어요`, sub: `${formatTitle(modalData.metadata?.video)} · ${formatTime(modalData.targetTime)}` }
                    : { kind: 'err', title: '클립보드 복사에 실패했어요', sub: '브라우저 권한을 확인해 주세요' })}
                />
              )}
            </div>
            <div className="script-box" ref={scriptContainerRef}>
              <div className="script-box-header">
                <h4><Icon name="script" /> 전체 대본 <small>하이라이트: 검색된 장면</small></h4>
                {modalSelectedLines.size > 0 && (
                  <div className="selective-copy-bar">
                    <span className="selective-copy-count">{modalSelectedLines.size}줄 선택됨</span>
                    <button className="selective-copy-btn" onClick={() => setCopyFormatPicker(true)}><Icon name="copy" /> 선택 복사</button>
                    <button className="selective-clear-btn" onClick={() => setModalSelectedLines(new Map())}>✕</button>
                  </div>
                )}
                {copyFormatPicker && (
                  <div className="copy-format-picker">
                    <div className="copy-format-header">
                      <span>복사 형식을 선택하세요</span>
                      <button className="selective-clear-btn" onClick={() => setCopyFormatPicker(false)}>✕</button>
                    </div>
                    <div className="copy-format-options">
                      <div className="copy-format-option">
                        <div className="copy-format-label">대사만</div>
                        <pre className="copy-format-example">{
                          (() => {
                            const videoTitle = modalData?.metadata?.video || '';
                            const sorted = [...modalSelectedLines.entries()].sort((a,b)=>a[0]-b[0]).slice(0,3).map(([,v])=>v.text).join('\n');
                            const sourceUrl = modalData?.sourceUrl || '';
                            const firstTime = [...modalSelectedLines.entries()].sort((a,b)=>a[0]-b[0])[0]?.[1]?.time || 0;
                            const tsUrl = sourceUrl && (sourceUrl.includes('youtube.com') || sourceUrl.includes('youtu.be') || sourceUrl.includes('weverse.io'))
                              ? getVideoUrl(sourceUrl, firstTime)
                              : '';

                            let preview = '';
                            if (videoTitle) preview += videoTitle + '\n';
                            preview += sorted;
                            if (modalSelectedLines.size > 3) preview += '\n...';
                            if (tsUrl && tsUrl !== sourceUrl) preview += '\n\n' + tsUrl;
                            return preview;
                          })()
                        }</pre>
                        <button className="copy-format-btn" onClick={() => performModalCopy(false)}>대사만 복사</button>
                      </div>
                      <div className="copy-format-option">
                        <div className="copy-format-label">타임스탬프 포함</div>
                        <pre className="copy-format-example">{
                          (() => {
                            const videoTitle = modalData?.metadata?.video || '';
                            const sorted = [...modalSelectedLines.entries()].sort((a,b)=>a[0]-b[0]).slice(0,3).map(([,v])=>`[${formatTime(v.time)}] ${v.text}`).join('\n');
                            const sourceUrl = modalData?.sourceUrl || '';
                            const firstTime = [...modalSelectedLines.entries()].sort((a,b)=>a[0]-b[0])[0]?.[1]?.time || 0;
                            const tsUrl = sourceUrl && (sourceUrl.includes('youtube.com') || sourceUrl.includes('youtu.be') || sourceUrl.includes('weverse.io'))
                              ? getVideoUrl(sourceUrl, firstTime)
                              : '';

                            let preview = '';
                            if (videoTitle) preview += videoTitle + '\n';
                            preview += sorted;
                            if (modalSelectedLines.size > 3) preview += '\n...';
                            if (tsUrl && tsUrl !== sourceUrl) preview += '\n\n' + tsUrl;
                            return preview;
                          })()
                        }</pre>
                        <button className="copy-format-btn copy-format-btn-ts" onClick={() => performModalCopy(true)}>타임스탬프 포함 복사</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {modalData.fullScript && modalData.fullScript.length > 0 ? (
                modalData.fullScript.map((script, idx) => {
                  const isHighlight = Math.abs(script.time - modalData.targetTime) <= 10;
                  const isYouTube = modalData.sourceUrl && getVideoPlatform(modalData.sourceUrl) === 'YouTube';
                  const isWeverse = modalData.sourceUrl && getVideoPlatform(modalData.sourceUrl) === 'Weverse';
                  const tsUrl = (isYouTube || isWeverse) ? getVideoUrl(modalData.sourceUrl, script.time) : null;
                  return (
                    <div key={idx} data-time={script.time} className={`script-line ${isHighlight ? 'highlight-main' : ''} ${modalSelectedLines.has(idx) ? 'script-line-selected' : ''}`}>
                      <input
                        type="checkbox"
                        className="script-line-checkbox"
                        checked={modalSelectedLines.has(idx)}
                        onChange={() => {
                          setModalSelectedLines(prev => {
                            const next = new Map(prev);
                            if (next.has(idx)) next.delete(idx);
                            else next.set(idx, { text: script.text, time: script.time });
                            return next;
                          });
                        }}
                      />
                      {tsUrl ? (
                        <a
                          href={tsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="time-tag time-tag-link"
                          title="이 구간으로 이동"
                        >[{formatTime(script.time)}]</a>
                      ) : (
                        <span className="time-tag">[{formatTime(script.time)}]</span>
                      )}
                      <span className="script-text"><Highlight text={script.text} matcher={highlighter} /></span>
                      <span className="line-actions">
                        <button
                          className="line-action-btn"
                          onClick={(e) => handleModalLineCopy(e, script.text)}
                          title={modalSelectedLines.size > 0 ? '선택한 대사 복사' : '이 대사 복사'}
                        ><Icon name="copy" /><span className="line-action-label">복사</span></button>
                        {tsUrl && (
                          <button
                            className="line-action-btn"
                            onClick={(e) => {
                              if (modalSelectedLines.size > 0) { e.stopPropagation(); setCopyFormatPicker(true); }
                              else handleShare(e, tsUrl, formatTitle(modalData.metadata?.video));
                            }}
                            title={modalSelectedLines.size > 0 ? '선택한 대사 복사' : '이 구간 링크 공유'}
                          ><Icon name="share" /><span className="line-action-label">공유</span></button>
                        )}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="no-script-box">
                  {modalData.noScriptReason === 'not_found' ? (
                    <>
                      <p>📂 자막 데이터가 없는 영상입니다.</p>
                      <small>이미지 검색으로 찾은 영상이지만 자막이 등록되지 않았습니다.</small>
                      <small className="no-script-filename">{modalData.metadata?.video}</small>
                    </>
                  ) : (
                    <>
                      <p>📭 자막 내용이 비어 있습니다.</p>
                      <small>영상 데이터는 있지만 자막 내용을 불러올 수 없습니다.</small>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DM 컨텍스트 모달 */}
      {dmModalData && (
        <div className="modal-overlay" onClick={closeDmModal}>
          <div className="modal-content dm-context-modal" onClick={e => e.stopPropagation()}>
            <button className="close-btn" onClick={closeDmModal} aria-label="닫기"><Icon name="close" /></button>
            <div className="modal-header">
              <h3><Icon name="chat" /> {dmModalData.target.member}님의 대화 흐름</h3>
              <span className="modal-time">앞뒤 50개 메시지</span>
            </div>
            <div className="script-box" ref={dmContextRef}>
              {dmModalData.context.map((msg, idx) => {
                const isTarget = msg === dmModalData.target;
                return (
                  <div key={idx} className={`dm-context-item ${isTarget ? 'dm-highlight' : ''}`}>
                    <div className="dm-context-meta">
                      <span className={`dm-badge ${getMemberClass(msg.member)}`}>{msg.member}</span>
                      <span className="dm-date">{msg.date} {msg.time}</span>
                    </div>
                    <div className="dm-context-text">
                      {msg.content.split('\n').map((line, i) => (
                        <div key={i} className={line.startsWith('↩') ? 'dm-reply-line' : ''}>{line}</div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <header className="App-header">
        {notice && (
          <div className={`notice-banner ${notice.color || 'blue'}`}>
            <div className="notice-text-group">
              {notice.message.split('\\n').map((l, i) => <p key={i} className="notice-text-line">{l}</p>)}
            </div>
            <div className="notice-links-container">
              {notice.link1 && <a href={notice.link1.url} target="_blank" rel="noopener noreferrer" className="notice-link">{notice.link1.text}</a>}
              {notice.link2 && <a href={notice.link2.url} target="_blank" rel="noopener noreferrer" className="notice-link">{notice.link2.text}</a>}
              {notice.link3 && <a href={notice.link3.url} target="_blank" rel="noopener noreferrer" className="notice-link">{notice.link3.text}</a>}
            </div>
          </div>
        )}

        <h1>Fromis_9 <em>통합 컨텐츠 검색</em></h1>

        {(!hasSearched || selectedImage) ? (
          <LucianImageDrop
            inputId="file-upload"
            status={isModelReady ? 'ready' : modelError ? 'error' : 'wait'}
            waitLabel={!modelProgress?.total ? 'AI 모델을 불러오는 중이에요'
              : modelProgress.loaded >= modelProgress.total ? 'AI 초기화 중 · 곧 끝나요'
              : `AI 다운로드 ${Math.round(modelProgress.loaded / modelProgress.total * 100)}% · ${Math.round(modelProgress.loaded / 1048576)}/${Math.round(modelProgress.total / 1048576)}MB`}
            waitProgress={modelProgress?.total ? Math.min(1, modelProgress.loaded / modelProgress.total) : 0}
            onFile={handleImageFile}
            onRetryModel={loadModel}
            onRetryJob={j => j.file && executeIntegratedSearch(j.file)}
            jobs={isLoading || selectedImage ? imageJobs : imageJobs.filter(j => j.state !== 'run')}
          />
        ) : (
        <div className="upload-container">
          {modelError && !isModelReady ? (
            <button type="button" className="legacy-upload-btn error" onClick={loadModel} title={modelError}>
              <Icon name="camera" /> AI 로드 실패 — 눌러서 다시 시도
            </button>
          ) : (
            <label htmlFor="file-upload" className={`legacy-upload-btn ${!isModelReady || isLoading ? 'disabled' : ''}`}>
              <Icon name="camera" />
              {isLoading ? ' 검색 중...'
                : isModelReady ? ' 사진으로 검색'
                : !modelProgress?.total ? ' AI 준비 중...'
                : modelProgress.loaded >= modelProgress.total ? ' AI 초기화 중...'
                : ` AI 다운로드 ${Math.round(modelProgress.loaded / modelProgress.total * 100)}% (${Math.round(modelProgress.loaded / 1048576)}/${Math.round(modelProgress.total / 1048576)}MB)`}
              {!isModelReady && modelProgress?.total > 0 && (
                <span className="model-progress" style={{ width: `${Math.min(100, modelProgress.loaded / modelProgress.total * 100)}%` }} />
              )}
            </label>
          )}
          <input id="file-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} disabled={!isModelReady || isLoading} />
        </div>
        )}

        {previewUrl && (
          <div className="preview-card">
            <img src={previewUrl} alt="Preview" className="result-thumb" />
            <div className="preview-info">
              <span>{isLoading ? '검색 중...' : `검색 완료${searchElapsedMs !== null ? ` · ${searchElapsedMs < 1000 ? `${searchElapsedMs}ms` : `${(searchElapsedMs / 1000).toFixed(1)}s`}` : ''}`}</span>
              <button onClick={() => {
                if (imageHistoryRef.current) {
                  window.history.back();
                } else {
                  setSelectedImage(null);
                  setPreviewUrl(null);
                  setResults([]);
                  setAllSearchResults([]);
                  setDmResults([]);
                  setHasSearched(false);
                }
              }}>취소</button>
            </div>
          </div>
        )}

        {!selectedImage && (
          <div className="search-box">
            <div className="search-input-wrap">
              <Icon name="search" className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleTextSearch()}
                placeholder="찾고자 하는 대사 입력"
              />
              {query && (
                <button className="clear-btn" onClick={() => {
                  setQuery('');
                  setActiveSearch(null);
                  setResults([]);
                  setAllSearchResults([]);
                  setDmResults([]);
                  setHasSearched(false);
                  searchInputRef.current?.focus();
                }} aria-label="검색어 지우기"><Icon name="close" /></button>
              )}
            </div>
            <button className="search-btn" onClick={handleTextSearch}>검색</button>
          </div>
        )}

        {!selectedImage && !hasSearched && recentSearches.length > 0 && (
          <div className="chip-row recent-row">
            <span className="chip-label">최근 검색</span>
            {recentSearches.map(s => (
              <button key={s} className="chip" onClick={() => handleTextSearch(s)}>{s}</button>
            ))}
            <button
              className="chip chip-muted"
              onClick={() => { setRecentSearches([]); try { localStorage.removeItem(RECENT_KEY); } catch {} }}
            >지우기</button>
          </div>
        )}

        {/* 추천 영상 (검색 전 초기 화면) */}
        {!hasSearched && !selectedImage && recommendedVideos.length > 0 && (
          <div className="recommended-section">
            <h2 className="section-title">추천 영상 <small>{recommendedVideos.length}개</small></h2>
            <div className="results-grid">
              {recommendedVideos.map((video) => {
                const effectiveUrl = video.sourceUrl || (Array.isArray(video.content)
                  ? [...video.content].reverse().find(i => typeof i === 'string' && i.startsWith('http'))
                  : null);
                const thumb = video.thumb || getYoutubeThumbnail(effectiveUrl);
                const platform = getVideoPlatform(effectiveUrl);
                const cls = { YouTube: 'yt', Weverse: 'wv', fromm: 'fm' }[platform] || 'wv';
                return (
                  <div
                    key={video.id ?? video.title}
                    className="card"
                    onClick={() => handleCardClick({
                      id: video.id,
                      metadata: { video: video.title, time: 0, preview: null },
                      sourceUrl: effectiveUrl,
                    })}
                  >
                    {thumb ? (
                      <img src={thumb} alt={video.title} className="result-thumb" onLoad={handleThumbLoad} />
                    ) : (
                      <div className={`rec-thumb-placeholder ${cls}`}>
                        <span>{platform}</span>
                      </div>
                    )}
                    <div className="card-body">
                      <h3 title={video.title}>{formatTitle(video.title)}</h3>
                      {platform && (
                        <a
                          href={video.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className={`video-link-btn ${cls} card-video-link`}
                        >
                          <Icon name="play" /> {platform}에서 보기
                        </a>
                      )}
                      <p className="click-hint">👆 클릭하여 전체 대본 보기</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(results.length > 0 || dmResults.length > 0) && !selectedImage && (
          <div className="toolbar-container">
            <button className="sort-btn" onClick={toggleSort}>
              <Icon name={sortOrder === 'desc' ? 'sortDesc' : 'sortAsc'} />
              <span>{sortOrder === 'desc' ? '최신순' : '과거순'}</span>
            </button>
          </div>
        )}

        {/* DM 검색 결과 */}
        {dmResults.length > 0 && !selectedImage && (
          <div className="dm-results-section">
            <h2 className="section-title dm-toggle-title" onClick={() => setIsDmExpanded(v => !v)}>
              <span><Icon name="chat" /> DM 메시지 검색 결과 <small>{dmResults.length}건</small></span>
              <span className="dm-toggle-arrow">{isDmExpanded ? '▲' : '▼'}</span>
            </h2>
            {!isDmExpanded && (
              <div className="dm-preview" onClick={() => setIsDmExpanded(true)} title="클릭해서 모두 보기">
                {dmResults.slice(0, 2).map((msg, i) => (
                  <div key={i} className="dm-preview-item">
                    <span className={`dm-badge ${getMemberClass(msg.member)}`}>{msg.member}</span>
                    <span className="dm-preview-text">
                      <Highlight text={msg.content.split('\n')[0]} matcher={highlighter} />
                    </span>
                  </div>
                ))}
                {dmResults.length > 2 && (
                  <div className="dm-preview-more">▼ {dmResults.length - 2}건 더 보기</div>
                )}
              </div>
            )}
            {isDmExpanded && (
              <>
                <div className="dm-list">
                  {dmResults.slice(0, dmPage * DM_ITEMS_PER_PAGE).map((msg, index) => (
                    <div
                      key={index}
                      className="dm-item"
                      onClick={() => handleDmClick(msg)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="dm-meta">
                        <span className={`dm-badge ${getMemberClass(msg.member)}`}>{msg.member}</span>
                        <span className="dm-date">{msg.date} {msg.time}</span>
                      </div>
                      <div className="dm-content">
                        {msg.content.split('\n').map((line, i) => (
                          <div key={i} className={line.startsWith('↩') ? 'dm-reply-line' : ''}>
                            <Highlight text={line} matcher={highlighter} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {dmResults.length > dmPage * DM_ITEMS_PER_PAGE && (
                  <button
                    className="dm-more-btn"
                    onClick={() => setDmPage(prev => prev + 1)}
                  >
                    👇 메시지 더 보기 ({Math.min((dmPage + 1) * DM_ITEMS_PER_PAGE, dmResults.length)} / {dmResults.length})
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* 연도 필터 + 결과 카운트 */}
        {allSearchResults.length > 0 && (
          <>
            {availableYears.length > 1 && (
              <div className="year-filter-container">
                <button
                  className={`year-btn ${!selectedYear ? 'active' : ''}`}
                  onClick={() => handleYearFilter(null)}
                >전체</button>
                {availableYears.map(year => (
                  <button
                    key={year}
                    className={`year-btn ${selectedYear === year ? 'active' : ''}`}
                    onClick={() => handleYearFilter(year)}
                  >{year}</button>
                ))}
              </div>
            )}
            <p className="result-count-info">
              영상 검색 결과 <strong>{results.length}</strong> / <strong>{filteredTotal}</strong>건
              {selectedYear && filteredTotal !== allSearchResults.length && (
                <span className="year-filter-total"> (전체 {allSearchResults.length}건)</span>
              )}
              {searchElapsedMs !== null && (
                <span className="search-elapsed"> · {searchElapsedMs < 1000 ? `${searchElapsedMs}ms` : `${(searchElapsedMs / 1000).toFixed(1)}s`}</span>
              )}
            </p>
          </>
        )}

        {/* 결과 그리드 */}
        <div className="results-grid">
          {results.map((item) => {
            const linkProps = getCardLinkProps(item);
            const cardKey = item.id != null ? item.id : `${item.metadata?.video}-${item.metadata?.time}`;
            const cardExpandId = item.id ?? item.metadata?.video;
            return (
              <div key={cardKey} className="card-wrapper">
                <div className="card" onClick={() => handleCardClick(item)}>
                  {item.metadata?.preview
                    ? <img src={getImageSrc(item.metadata.preview)} alt="result" className="result-thumb" onLoad={handleThumbLoad} />
                    : videoThumbMap[item.metadata?.video]
                      ? <img src={videoThumbMap[item.metadata?.video]} alt="result" className="result-thumb" onLoad={handleThumbLoad} />
                      : (() => {
                          const yt = getYoutubeThumbnail(item.sourceUrl || item.metadata?.sourceUrl);
                          return yt ? <img src={yt} alt="result" className="result-thumb" onLoad={handleThumbLoad} /> : null;
                        })()
                  }
                  <div className="card-body">
                    <h3 title={item.metadata?.video}>{formatTitle(item.metadata?.video)}</h3>
                    {!selectedImage && item.score === MATCH.FUZZY && (
                      <span className="fuzzy-badge" title="검색어와 받침만 다른 유사 표현이 일치했습니다">≈ 유사 일치</span>
                    )}
                    {item.snippet ? (
                      <div className="snippet-row">
                        <p className="snippet-text">💬 "<Highlight text={item.snippet} matcher={highlighter} />"</p>
                        {(() => {
                          const tsUrl = getVideoUrl(item.sourceUrl, item.metadata?.time);
                          const platform = getVideoPlatform(item.sourceUrl);
                          return tsUrl && platform !== 'fromm' ? (
                            <a
                              href={tsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="timestamp-link"
                              onClick={e => e.stopPropagation()}
                              title={`${platform}에서 해당 시간으로 이동`}
                            ><Icon name="play" /> {formatTime(item.metadata?.time)}</a>
                          ) : null;
                        })()}
                      </div>
                    ) : (
                      <p className="time-info">{formatTime(item.metadata?.time)}</p>
                    )}
                    <div className="button-group">
                      {linkProps && (
                        <a
                          href={linkProps.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className={`video-link-btn ${linkProps.cls} card-video-link`}
                        >
                          <Icon name="play" /> {linkProps.platform}에서 보기
                        </a>
                      )}
                      <button className="copy-link-btn" onClick={(e) => handleCopyLink(e, item)}>
                        <Icon name="link" /> 링크 복사
                      </button>
                      {item.subMatches && item.subMatches.length > 0 && (
                        <button
                          className="more-matches-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = expandedCardId === cardExpandId ? null : cardExpandId;
                            setExpandedCardId(next);
                            setSelectedSnippets(new Map());
                          }}
                        >
                          + 외 {item.subMatches.length}개
                        </button>
                      )}
                    </div>
                    <p className="click-hint">👆 클릭하여 전체 대본 보기</p>
                  </div>
                </div>

                {expandedCardId === cardExpandId && item.subMatches && (
                  <div className="sub-matches-list">
                    {selectedSnippets.size > 0 && (
                      <div className="selective-copy-bar">
                        <span className="selective-copy-count">{selectedSnippets.size}개 선택됨</span>
                        <button className="selective-copy-btn" onClick={handleSelectiveCopy}>
                          <Icon name="copy" /> 선택 복사
                        </button>
                        <button className="selective-clear-btn" onClick={() => setSelectedSnippets(new Map())}>
                          ✕
                        </button>
                      </div>
                    )}
                    {item.subMatches.map((sub, idx) => {
                      const snippetKey = `${cardExpandId}-${idx}`;
                      const tsUrl = getVideoUrl(sub.sourceUrl, sub.metadata.time);
                      const platform = getVideoPlatform(sub.sourceUrl);
                      return (
                        <div key={idx} className="sub-match-item">
                          <input
                            type="checkbox"
                            className="sub-match-checkbox"
                            checked={selectedSnippets.has(snippetKey)}
                            onClick={e => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              setSelectedSnippets(prev => {
                                const next = new Map(prev);
                                if (next.has(snippetKey)) next.delete(snippetKey);
                                else next.set(snippetKey, sub.snippet);
                                return next;
                              });
                            }}
                          />
                          <div className="sub-match-content" onClick={() => handleCardClick(sub)}>
                            <span className="sub-time"><Icon name="clock" /> {formatTime(sub.metadata.time)}</span>
                            <span className="sub-text">"<Highlight text={sub.snippet} matcher={highlighter} />"</span>
                          </div>
                          {tsUrl && platform !== 'fromm' && (
                            <a
                              href={tsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="timestamp-link"
                              onClick={e => e.stopPropagation()}
                              title={`${platform}에서 해당 시간으로 이동`}
                            ><Icon name="play" /></a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {results.length < filteredTotal && (
          <div className="load-more-container" style={{ textAlign: 'center', margin: '30px 0' }}>
            <button onClick={handleLoadMore} className="load-more-btn">
              👇 영상 더 보기 ({results.length} / {filteredTotal})
            </button>
          </div>
        )}

        {hasSearched && allSearchResults.length === 0 && dmResults.length === 0 && !isLoading && (
          <LucianNoResults
            query={activeSearch ? activeSearch.tokens.join(' ') : query}
            isImage={!!selectedImage}
            drops={noResultHints.drops}
            shorter={noResultHints.shorter}
            popular={SUGGESTED_KEYWORDS.slice(0, 5)}
            onSearch={handleTextSearch}
          />
        )}

      </header>

      <nav className="tabbar" aria-label="빠른 이동">
        <button type="button" className={!hasSearched ? 'on' : ''} onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); searchInputRef.current?.focus(); }}>
          <Icon name="search" />검색
        </button>
        <button type="button" disabled={dmResults.length === 0} onClick={() => document.querySelector('.dm-results-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
          <Icon name="chat" />DM
        </button>
        <button type="button" disabled={!isModelReady || isLoading} onClick={() => document.getElementById('file-upload')?.click()}>
          <Icon name="photo" />사진
        </button>
        <button type="button" disabled={hasSearched} onClick={() => document.querySelector('.recommended-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
          <Icon name="home" />추천
        </button>
      </nav>
    </div>
  );
}

export default App;
